import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { trackCTAClick } from 'utils/analytics';
import { interpolate } from 'utils/i18nUtils';
import { useSeoContentHide } from 'hooks';
import GoogleLoginButton from 'components/shared/GoogleLoginButton';

const PATHS = { TRY: 'try', TEAM: 'team', JOIN: 'join' };

const useScrollReveal = (dep) => {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = ref.current?.querySelectorAll('.scroll-reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [dep]);

  return ref;
};

const CTA_URL = '/users/auth/google_oauth2?role=teacher';

// ─── SVG Icons ──────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg className="w-5 h-5 flex-shrink-0 text-[#3366FF]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ArrowRightIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const SparklesIcon = () => (
  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const SECTION_ICONS = {
  target: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  book: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
  ),
  currency: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  bolt: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
    </svg>
  ),
  palette: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
    </svg>
  ),
  clipboard: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
    </svg>
  ),
  creditCard: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
    </svg>
  ),
  chart: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
    </svg>
  ),
  search: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  ),
  pencil: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  ),
  rocket: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
    </svg>
  ),
  gift: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
    </svg>
  ),
  handshake: (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
    </svg>
  ),
};

const ICON_MAP = {
  collaborate: [SECTION_ICONS.palette, SECTION_ICONS.gift, SECTION_ICONS.rocket],
  recruitingAssistant: [SECTION_ICONS.pencil, SECTION_ICONS.search, SECTION_ICONS.rocket],
  join: [SECTION_ICONS.search, SECTION_ICONS.pencil, SECTION_ICONS.rocket],
  winWin: [SECTION_ICONS.pencil, SECTION_ICONS.target, SECTION_ICONS.chart, SECTION_ICONS.handshake],
  collaborateFeatures: [SECTION_ICONS.palette, SECTION_ICONS.clipboard, SECTION_ICONS.creditCard, SECTION_ICONS.chart],
  joinBenefits: [SECTION_ICONS.target, SECTION_ICONS.book, SECTION_ICONS.currency, SECTION_ICONS.bolt],
  transfer: [SECTION_ICONS.book, SECTION_ICONS.handshake, SECTION_ICONS.target],
};

// ─── Shared Components ──────────────────────────────────────────────

const CtaButton = ({ userSignedIn, className, style, trackLabel, children }) => {
  if (userSignedIn) {
    return (
      <a
        // eslint-disable-next-line no-restricted-syntax -- the backstage has no locale routes, so a prefix here is a 404
        href="/dashboard"
        className={className}
        style={style}
        onClick={() => trackCTAClick('teacher_go_to_dashboard', trackLabel, '/dashboard')}
      >
        {children}
      </a>
    );
  }
  return (
    <GoogleLoginButton
      role="teacher"
      onSubmit={() => trackCTAClick('teacher_create_team', trackLabel, CTA_URL)}
      className={className}
      style={style}
    >
      {children}
    </GoogleLoginButton>
  );
};

const IconCardGrid = ({ title, data, icons, sectionBg = 'bg-white', cardClassName = 'bg-gray-50 border border-gray-100', columns = 2 }) => {
  const items = [data?.item1, data?.item2, data?.item3, data?.item4].filter(Boolean);

  return (
    <section className={`py-20 ${sectionBg} overflow-hidden`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900">
            {title}
          </h2>
        </div>

        <div className={`grid grid-cols-1 gap-6 ${columns === 3 ? 'md:grid-cols-3' : 'sm:grid-cols-2'}`}>
          {items.map((item, i) => (
            <div
              key={i}
              className={`scroll-reveal delay-${(i + 1) * 100} rounded-2xl p-6 ${cardClassName}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-[#3366FF]" style={{ backgroundColor: '#E6F0FF' }}>
                  {icons?.[i] || SECTION_ICONS.target}
                </div>
                <div>
                  <h3 className="text-lg font-taipei-sans font-bold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const NumberedStepsSection = ({ title, items, sectionBg = 'bg-white' }) => (
  <section className={`py-20 ${sectionBg} overflow-hidden`}>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900">
          {title}
        </h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {items.map(({ data, num, icon }, index) => (
          <div key={index} className={`scroll-reveal delay-${(index + 1) * 100} text-center`}>
            <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#E6F0FF] text-[#3366FF] mb-6">
              {icon}
              <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#3366FF] text-white text-xs font-bold flex items-center justify-center">
                {num}
              </span>
            </div>
            <h3 className="text-lg font-taipei-sans font-bold text-gray-900 mb-3">
              {data?.title}
            </h3>
            <p className="text-gray-600 leading-relaxed">
              {data?.description}
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Hero Section ───────────────────────────────────────────────────

const PathCard = ({ pathKey, icon, card, isActive, onClick, children }) => (
  <div
    data-path-card={pathKey}
    data-selected={isActive ? 'true' : 'false'}
    className={`flex flex-col rounded-2xl p-6 sm:p-8 cursor-pointer transition-all duration-300 ${
      isActive
        ? 'bg-white shadow-xl ring-2 ring-[#3366FF]'
        : 'bg-white/60 shadow-md hover:shadow-lg hover:bg-white/80'
    }`}
    // A click on the card's CTA signs up or opens the dashboard; it must not also switch the path.
    onClick={(e) => { if (!e.target.closest('[data-card-cta]')) onClick(); }}
  >
    <div className="flex flex-1 flex-col text-center">
      <div className={`inline-flex items-center justify-center self-center w-16 h-16 rounded-2xl mb-4 transition-colors duration-300 ${
        isActive ? 'bg-[#3366FF] text-white' : 'bg-[#E6F0FF] text-[#3366FF]'
      }`}>
        {icon}
      </div>
      <h2 className={`text-xl sm:text-2xl font-taipei-sans font-bold text-gray-900 text-balance break-keep ${card?.lead ? 'mb-2' : 'mb-4'}`}>
        {/* The card div handles mouse clicks; this button makes the card selectable from the keyboard.
            Its click bubbles up to the card's onClick, so it needs no handler of its own. */}
        <button type="button" aria-pressed={isActive} className="text-inherit font-inherit text-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#3366FF]">
          {card?.title}
        </button>
      </h2>
      {card?.lead && (
        <p data-card-lead className="text-sm sm:text-base text-[#3366FF] mb-4">
          {card.lead}
        </p>
      )}
      <ul className="flex-1 space-y-2.5 mb-6 text-left">
        {(card?.bullets || []).map((bullet, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm sm:text-base text-gray-600">
            <CheckIcon />
            {bullet}
          </li>
        ))}
      </ul>
      <div className="flex flex-col" data-card-cta>
        {children}
      </div>
    </div>
  </div>
);

const HeroSection = ({ hero, activePath, onSelectPath, userSignedIn }) => (
  <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #E6F0FF 0%, #f0f4ff 40%, #f8faff 70%, #ffffff 100%)' }}>
    <div className="absolute top-20 right-[10%] w-[500px] h-[500px] bg-[#3366FF] rounded-full opacity-[0.04] blur-[100px]" />
    <div className="absolute -bottom-20 left-[5%] w-[400px] h-[400px] bg-[#6699FF] rounded-full opacity-[0.06] blur-[80px]" />

    <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
      <div className="text-center mb-12">
        <h1 className="scroll-reveal text-3xl sm:text-4xl lg:text-5xl font-taipei-sans font-bold tracking-tight text-gray-900 mb-4 leading-[1.15] text-balance break-keep">
          {hero?.main_title}
        </h1>
        <p className="scroll-reveal delay-100 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10">
          {hero?.subtitle}
        </p>
        <p className="scroll-reveal delay-200 text-sm font-semibold tracking-[0.2em] uppercase text-[#3366FF]">
          {hero?.tagline}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        <PathCard
          pathKey={PATHS.TRY}
          icon={<SparklesIcon />}
          card={hero?.try_card}
          isActive={activePath === PATHS.TRY}
          onClick={() => onSelectPath(PATHS.TRY)}
        >
          <CtaButton
            userSignedIn={userSignedIn}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-base font-taipei-sans font-bold text-white transition-all duration-200 hover:brightness-110 cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)',
              boxShadow: '0 4px 14px rgba(51, 102, 255, 0.35)',
            }}
            trackLabel="for_teachers_hero_try"
          >
            {hero?.try_card?.cta}
            <ArrowRightIcon className="w-4 h-4" />
          </CtaButton>
        </PathCard>

        <PathCard
          pathKey={PATHS.TEAM}
          icon={<UsersIcon />}
          card={hero?.team_card}
          isActive={activePath === PATHS.TEAM}
          onClick={() => onSelectPath(PATHS.TEAM)}
        >
          <CtaButton
            userSignedIn={userSignedIn}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-base font-taipei-sans font-bold border-2 transition-all duration-200 hover:bg-[#E6F0FF] cursor-pointer"
            style={{ borderColor: '#3366FF', color: '#3366FF' }}
            trackLabel="for_teachers_hero_team"
          >
            {hero?.team_card?.cta}
            <ArrowRightIcon className="w-4 h-4" />
          </CtaButton>
        </PathCard>
      </div>

      <p className="mt-6 text-center text-sm sm:text-base text-gray-500">
        <button
          type="button"
          data-join-link
          aria-current={activePath === PATHS.JOIN ? 'true' : undefined}
          className={`inline-flex min-h-[44px] items-center underline underline-offset-4 text-[#3366FF] hover:text-[#2952CC] ${activePath === PATHS.JOIN ? 'font-bold' : ''}`}
          onClick={() => {
            trackCTAClick('teacher_view_join_path', 'for_teachers_hero_join_link');
            onSelectPath(PATHS.JOIN);
          }}
        >
          {hero?.join_link}
        </button>
      </p>
    </div>
  </section>
);

// ─── Video Section ──────────────────────────────────────────────────

const VideoSection = () => (
  <section className="py-12 bg-white overflow-hidden">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="scroll-reveal rounded-2xl shadow-lg overflow-hidden aspect-video">
        <iframe
          className="w-full h-full"
          src="https://www.youtube-nocookie.com/embed/HXoGQUbKpkI"
          title="TutorNetworking 介紹影片"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  </section>
);

// ─── Better Deal Section ────────────────────────────────────────────

const HeroFeeCard = ({ hero, fees }) => (
  <div
    className="scroll-reveal rounded-2xl border-2 shadow-lg p-8 text-center bg-white"
    style={{ borderColor: '#3366FF' }}
  >
    <div className="mb-4">
      <span className="text-sm font-medium" style={{ color: '#3366FF' }}>
        {interpolate(hero?.label, fees)}
      </span>
      <span className="text-sm text-gray-500 ml-1">
        {interpolate(hero?.label_hint, fees)}
      </span>
    </div>

    <p className="text-lg text-gray-600 mb-1">{interpolate(hero?.keep_prefix, fees)}</p>
    <p className="text-5xl sm:text-6xl font-taipei-sans font-bold mb-4" style={{ color: '#3366FF' }}>
      {interpolate(hero?.keep_rate, fees)}
    </p>

    <span
      className="inline-block text-sm px-3 py-1 rounded-full font-medium mb-6"
      style={{ backgroundColor: '#E6F0FF', color: '#3366FF' }}
    >
      {interpolate(hero?.platform_fee_line, fees)}
    </span>

    <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: '#F8FAFF' }}>
      <p className="text-base font-medium text-gray-900">
        {interpolate(hero?.example, fees)}
      </p>
    </div>

    <p className="text-sm text-gray-500">
      {interpolate(hero?.fee_detail, fees)}
    </p>
  </div>
);

const ComparisonRow = ({ comparison, fees }) => {
  const ps = comparison?.platform_student || {};
  const op = comparison?.other_platform || {};

  return (
    <div className="scroll-reveal delay-100 grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="rounded-2xl bg-white border border-gray-200 p-6 text-center" style={{ borderLeftWidth: '3px', borderLeftColor: '#3366FF' }}>
        <div className="mb-2">
          <span className="text-sm font-medium text-gray-900">
            {interpolate(ps.label, fees)}
          </span>
          <span className="text-sm text-gray-500 ml-1">
            {interpolate(ps.label_hint, fees)}
          </span>
        </div>
        <p className="text-2xl font-taipei-sans font-bold text-gray-900 mb-2">
          {interpolate(ps.keep_rate, fees)}
        </p>
        <p className="text-sm text-gray-500">
          {interpolate(ps.detail, fees)}
        </p>
      </div>

      <div className="rounded-2xl bg-white border border-gray-200 p-6 text-center">
        <p className="text-sm font-medium text-gray-500 mb-2">
          {interpolate(op.label, fees)}
        </p>
        <p className="text-2xl font-taipei-sans font-bold text-gray-400 line-through mb-2">
          {interpolate(op.rate, fees)}
        </p>
        <p className="text-sm text-gray-400">
          {interpolate(op.keep_note, fees)}
        </p>
      </div>
    </div>
  );
};

const TransferFlow = ({ steps }) => {
  const items = [
    { text: steps.step1, icon: ICON_MAP.transfer[0] },
    { text: steps.step2, icon: ICON_MAP.transfer[1] },
    { text: steps.step3, icon: ICON_MAP.transfer[2] },
  ];

  return (
    <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-2">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          <div className="scroll-reveal flex flex-col items-center text-center max-w-[200px]">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 text-[#3366FF]" style={{ backgroundColor: '#E6F0FF' }}>
              {item.icon}
            </div>
            <p className="text-sm font-medium text-gray-900">{item.text}</p>
          </div>
          {i < items.length - 1 && (
            <ArrowRightIcon className="w-6 h-6 flex-shrink-0 rotate-90 md:rotate-0 text-[#3366FF]" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const BetterDealSection = ({ betterDeal, fees, userSignedIn }) => {
  const fc = betterDeal.fee_comparison || {};
  const st = betterDeal.student_transfer || {};

  return (
    <section className="py-20 bg-gray-50 overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900">
            {betterDeal.title}
          </h2>
        </div>

        <div className="mb-20">
          <h3 className="scroll-reveal text-xl sm:text-2xl font-taipei-sans font-bold text-gray-900 text-center mb-10">
            {fc.subtitle}
          </h3>
          <div className="max-w-md mx-auto mb-6">
            <HeroFeeCard hero={fc.hero} fees={fees} />
          </div>
          <ComparisonRow comparison={fc.comparison} fees={fees} />
        </div>

        <div>
          <h3 className="scroll-reveal text-xl sm:text-2xl font-taipei-sans font-bold text-gray-900 text-center mb-4">
            {st.subtitle}
          </h3>
          <p className="scroll-reveal text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            {st.description}
          </p>
          <TransferFlow steps={st} />
        </div>

        <div className="scroll-reveal flex flex-col sm:flex-row items-center justify-center gap-4 mt-16">
          <a
            // eslint-disable-next-line no-restricted-syntax -- /teams/join has no route inside the locale scope, so a prefix here is a 404
            href="/teams/join"
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-base font-taipei-sans font-bold border-2 transition-all duration-200 hover:bg-[#E6F0FF]"
            style={{ borderColor: '#3366FF', color: '#3366FF' }}
            onClick={() => trackCTAClick('teacher_explore_teams', 'for_teachers_better_deal', '/teams/join')}
          >
            {betterDeal.cta_explore}
          </a>
          <CtaButton
            userSignedIn={userSignedIn}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl text-base font-taipei-sans font-bold text-white transition-all duration-200 hover:brightness-110 cursor-pointer"
            style={{
              background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)',
              boxShadow: '0 4px 14px rgba(51, 102, 255, 0.35)',
            }}
            trackLabel="for_teachers_better_deal"
          >
            {betterDeal.cta_create}
          </CtaButton>
        </div>
      </div>
    </section>
  );
};

// ─── Revenue Flow Section ───────────────────────────────────────────

const FlowStep = ({ step, isLast }) => (
  <div className="scroll-reveal flex items-start gap-4">
    <div className="flex flex-col items-center flex-shrink-0">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
        style={{ background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)' }}
      >
        {step.num}
      </div>
      {!isLast && <div className="w-0.5 h-12 bg-[#3366FF]/20 mt-1" />}
    </div>

    <div className="pb-8">
      <div className="flex items-center gap-3 mb-1">
        <h4 className="font-taipei-sans font-bold text-gray-900">{step.label}</h4>
        <span
          className="text-xs px-2 py-0.5 rounded-full font-medium"
          style={{ backgroundColor: '#E6F0FF', color: '#3366FF' }}
        >
          {step.time}
        </span>
      </div>
      <p className="text-sm text-gray-600">{step.description}</p>
    </div>
  </div>
);

const RevenueFlowSection = ({ revenueFlow }) => {
  const flowSteps = [
    { num: 1, ...revenueFlow.step1 },
    { num: 2, ...revenueFlow.step2 },
    { num: 3, ...revenueFlow.step3 },
    { num: 4, ...revenueFlow.step4 },
    { num: 5, ...revenueFlow.step5 },
  ];

  return (
    <section className="py-20 bg-gray-50 overflow-hidden">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900">
            {revenueFlow.title}
          </h2>
        </div>

        <div className="max-w-xl mx-auto">
          {flowSteps.map((step, i) => (
            <FlowStep key={i} step={step} isLast={i === flowSteps.length - 1} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ─── Collaborate CTA Section ────────────────────────────────────────

const CtaSection = ({ cta, userSignedIn }) => {
  const features = [cta.feature1, cta.feature2, cta.feature3, cta.feature4].filter(Boolean);

  return (
    <section className="py-20 overflow-hidden relative">
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, #0a1628 0%, #16213e 50%, #1a1a2e 100%)' }}
      />
      <div className="absolute top-10 left-[10%] w-64 h-64 bg-[#3366FF] rounded-full opacity-10 blur-3xl" />
      <div className="absolute bottom-10 right-[10%] w-48 h-48 bg-[#6699FF] rounded-full opacity-10 blur-3xl" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-white mb-3">
          {cta.title}
        </h2>
        <p className="scroll-reveal delay-100 text-xl sm:text-2xl font-taipei-sans text-[#6699FF] mb-6">
          {cta.subtitle}
        </p>
        <p className="scroll-reveal delay-200 text-lg text-gray-300 max-w-2xl mx-auto mb-10">
          {cta.description}
        </p>

        <div className="scroll-reveal delay-300 grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
          {features.map((feature, i) => (
            <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-sm text-gray-200 font-medium">{feature}</p>
            </div>
          ))}
        </div>

        <CtaButton
          userSignedIn={userSignedIn}
          className="scroll-reveal delay-400 inline-block px-10 py-4 rounded-xl text-lg font-taipei-sans font-bold text-white transition-all duration-200 hover:brightness-110 cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)',
            boxShadow: '0 0 40px rgba(51, 102, 255, 0.4), 0 15px 30px rgba(0, 0, 0, 0.3)',
          }}
          trackLabel="for_teachers_collaborate_cta"
        >
          {cta.button}
        </CtaButton>
      </div>
    </section>
  );
};

// ─── Join Path: Earnings Section ────────────────────────────────────

const JoinEarningsSection = ({ earnings }) => (
  <section className="py-20 overflow-hidden" style={{ backgroundColor: '#E6F0FF' }}>
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-gray-900 mb-6">
        {earnings?.title}
      </h2>
      <p className="scroll-reveal delay-100 text-lg text-gray-600 leading-relaxed">
        {earnings?.description}
      </p>
    </div>
  </section>
);

// ─── Join Path: CTA Section ─────────────────────────────────────────

const JoinCtaSection = ({ cta }) => (
  <section className="py-20 overflow-hidden relative">
    <div
      className="absolute inset-0"
      style={{ background: 'linear-gradient(135deg, #0a1628 0%, #16213e 50%, #1a1a2e 100%)' }}
    />
    <div className="absolute top-10 left-[10%] w-64 h-64 bg-[#3366FF] rounded-full opacity-10 blur-3xl" />
    <div className="absolute bottom-10 right-[10%] w-48 h-48 bg-[#6699FF] rounded-full opacity-10 blur-3xl" />

    <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="scroll-reveal text-3xl sm:text-4xl font-taipei-sans font-bold text-white mb-10">
        {cta?.title}
      </h2>
      <a
        // eslint-disable-next-line no-restricted-syntax -- /teams/join has no route inside the locale scope, so a prefix here is a 404
        href="/teams/join"
        className="scroll-reveal delay-100 inline-flex items-center gap-2 px-10 py-4 rounded-xl text-lg font-taipei-sans font-bold text-white transition-all duration-200 hover:brightness-110"
        style={{
          background: 'linear-gradient(135deg, #3366FF 0%, #2952CC 100%)',
          boxShadow: '0 0 40px rgba(51, 102, 255, 0.4), 0 15px 30px rgba(0, 0, 0, 0.3)',
        }}
        onClick={() => trackCTAClick('teacher_explore_teams', 'for_teachers_join_cta', '/teams/join')}
      >
        {cta?.button}
        <ArrowRightIcon className="w-5 h-5" />
      </a>
    </div>
  </section>
);

// ─── Main Component ─────────────────────────────────────────────────

const TRY_STEPS = [
  { data: null, num: '01', icon: ICON_MAP.collaborate[0] },
  { data: null, num: '02', icon: ICON_MAP.collaborate[1] },
  { data: null, num: '03', icon: ICON_MAP.collaborate[2] },
];

const JOIN_STEPS = [
  { data: null, num: '01', icon: ICON_MAP.join[0] },
  { data: null, num: '02', icon: ICON_MAP.join[1] },
  { data: null, num: '03', icon: ICON_MAP.join[2] },
];

const ForTeachersPage = ({ translations }) => {
  const [activePath, setActivePath] = useState(PATHS.TRY);
  const scrollRef = useScrollReveal(activePath);
  const contentRef = useRef(null);
  const isFirstRender = useRef(true);
  const t = translations.for_teachers || {};
  const fees = translations.fees || {};
  const userSignedIn = translations.user_signed_in;

  // Hide the server-rendered SEO fallback (H1) once React takes over.
  useSeoContentHide();

  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activePath]);

  const handleSelectPath = (path) => {
    if (path === activePath || !Object.values(PATHS).includes(path)) return;
    setActivePath(path);
  };

  const steps = t.steps || {};
  const tryStepItems = TRY_STEPS.map((s, i) => ({
    ...s,
    data: steps[`step${i + 1}`],
  }));

  const hw = t.join_path?.how_it_works || {};
  const joinStepItems = JOIN_STEPS.map((s, i) => ({
    ...s,
    data: hw[`step${i + 1}`],
  }));

  return (
    <div className="min-h-screen" ref={scrollRef}>
      <HeroSection
        hero={t.hero || {}}
        activePath={activePath}
        onSelectPath={handleSelectPath}
        userSignedIn={userSignedIn}
      />

      <VideoSection />

      {/* key forces remount so scroll-reveal animations re-trigger on path switch */}
      <div ref={contentRef} key={activePath} data-active-path={activePath}>
        {activePath === PATHS.TRY && (
          <>
            <div data-section="steps">
              <NumberedStepsSection title={steps.title} items={tryStepItems} />
            </div>
            <div data-section="recruiting-assistant">
              <IconCardGrid
                title={t.recruiting_assistant?.title}
                data={t.recruiting_assistant}
                icons={ICON_MAP.recruitingAssistant}
                sectionBg="bg-gray-50"
                cardClassName="bg-white border border-gray-100 shadow-sm"
                columns={3}
              />
            </div>
            <div data-section="features">
              <IconCardGrid
                title={t.collaborate_features?.title}
                data={t.collaborate_features}
                icons={ICON_MAP.collaborateFeatures}
                cardClassName="bg-gray-50 border border-gray-100"
              />
            </div>
            <div data-section="cta">
              <CtaSection cta={t.cta || {}} userSignedIn={userSignedIn} />
            </div>
          </>
        )}
        {activePath === PATHS.TEAM && (
          <>
            <div data-section="win-win">
              <IconCardGrid
                title={t.win_win?.title}
                data={t.win_win}
                icons={ICON_MAP.winWin}
                cardClassName="bg-gray-50 border border-gray-100"
              />
            </div>
            <div data-section="revenue-flow">
              <RevenueFlowSection revenueFlow={t.revenue_flow || {}} />
            </div>
            <div data-section="cta">
              <CtaSection cta={t.cta || {}} userSignedIn={userSignedIn} />
            </div>
          </>
        )}
        {activePath === PATHS.JOIN && (
          <>
            <div data-section="join-benefits">
              <IconCardGrid
                title={t.join_path?.benefits?.title}
                data={t.join_path?.benefits}
                icons={ICON_MAP.joinBenefits}
                sectionBg="bg-gray-50"
                cardClassName="bg-white border border-gray-100 shadow-sm"
              />
            </div>
            <div data-section="join-steps">
              <NumberedStepsSection title={hw.title} items={joinStepItems} />
            </div>
            <div data-section="join-earnings">
              <JoinEarningsSection earnings={t.join_path?.earnings || {}} />
            </div>
            <div data-section="join-cta">
              <JoinCtaSection cta={t.join_path?.cta || {}} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForTeachersPage;
